import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SharedStream } from '@/lib/shared-types';
import { dashboardApi } from '@/lib/services/dashboard-api';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Video, ArrowRight, Clock, Eye, Play, Calendar } from 'lucide-react';
import { MediaImage } from '@/components/ui/media-image';
import { formatDistanceToNowSafe } from '@/lib/utils/date';

interface LatestStreamsWidgetProps {
  onViewMore?: () => void;
  maxStreams?: number;
}

export function LatestStreamsWidget({ onViewMore, maxStreams = 6 }: LatestStreamsWidgetProps) {
  const navigate = useNavigate();
  const [streams, setStreams] = useState<SharedStream[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLatestStreams();
  }, []);

  const fetchLatestStreams = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get recent streams accessible to the current user
      const data = await dashboardApi.getRecentStreams(maxStreams, false);
      setStreams(data);
    } catch (err) {
      console.error('Error fetching latest streams:', err);
      setError('Failed to load latest streams');
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinStream = async (streamId: number) => {
    try {
      const url = await dashboardApi.joinStream(streamId);
      if (url) {
        navigate(`/streams/${streamId}`);
      }
    } catch {
      toast.error('Failed to join stream');
      navigate(`/streams/${streamId}`);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'live':
        return <Badge variant="destructive" className="animate-pulse text-xs px-2 py-1">LIVE</Badge>;
      case 'scheduled':
        return <Badge variant="secondary" className="text-xs px-2 py-1">Scheduled</Badge>;
      case 'ended':
        return <Badge variant="outline" className="text-xs px-2 py-1">Ended</Badge>;
      default:
        return <Badge variant="outline" className="text-xs px-2 py-1">{status}</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'live':
        return <Play className="h-4 w-4 text-destructive" />;
      case 'scheduled':
        return <Clock className="h-4 w-4" />;
      case 'ended':
        return <Calendar className="h-4 w-4" />;
      default:
        return <Play className="h-4 w-4" />;
    }
  };

  const formatScheduledTime = (scheduledStart?: string) => {
    if (!scheduledStart) return 'Unknown time';
    const date = new Date(scheduledStart);
    if (Number.isNaN(date.getTime())) return 'Unknown time';
    const now = new Date();

    if (date > now) {
      return formatDistanceToNowSafe(date);
    } else {
      return `Started ${formatDistanceToNowSafe(date, 'Unknown time')}`;
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(maxStreams)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="aspect-video bg-muted rounded-lg mb-3"></div>
            <div className="space-y-2">
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
              <div className="h-3 bg-muted rounded w-1/4"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchLatestStreams}
        >
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <>
      {streams.length === 0 ? (
        <div className="text-center py-12">
          <Video className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-sm text-muted-foreground mb-4">No streams available yet</p>
          <p className="text-xs text-muted-foreground">
            Streams will appear here once they are created and published.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {streams.map((stream) => (
              <div
                key={stream.id}
                className="group cursor-pointer"
                onClick={() => navigate(`/streams/${stream.id}`)}
              >
                {/* Thumbnail */}
                <div className="aspect-video bg-linear-to-br from-muted to-muted/50 relative overflow-hidden rounded-lg mb-3 group-hover:shadow-lg transition-shadow duration-200">
                  {stream.thumbnailUrl ? (
                    <MediaImage
                      src={stream.thumbnailUrl}
                      alt={stream.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        {getStatusIcon(stream.status)}
                        <p className="text-sm text-muted-foreground mt-2">
                          {stream.status === 'live' ? 'Live Stream' : 'Scheduled'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Status Badge */}
                  <div className="absolute top-2 left-2 z-10">
                    {getStatusBadge(stream.status)}
                  </div>

                  {/* Viewer Count for Live Streams */}
                  {stream.status === 'live' && stream.viewerCount !== undefined && (
                    <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs backdrop-blur-sm">
                      <div className="flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        <span>{stream.viewerCount}</span>
                      </div>
                    </div>
                  )}

                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                    {stream.status === 'live' && (
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJoinStream(stream.id);
                        }}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Watch Live
                      </Button>
                    )}
                    {stream.status === 'scheduled' && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJoinStream(stream.id);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </Button>
                    )}
                    {stream.status === 'ended' && stream.playbackUrl && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJoinStream(stream.id);
                        }}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Watch Recording
                      </Button>
                    )}
                  </div>
                </div>

                {/* Stream Info */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">
                    {stream.title}
                  </h4>

                  {stream.creator && (
                    <p className="text-xs text-muted-foreground">
                      {stream.creator.name}
                    </p>
                  )}

                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {stream.scheduledStart ? (
                      <span>{formatScheduledTime(stream.scheduledStart)}</span>
                    ) : (
                      <span>{formatDistanceToNowSafe(stream.createdAt)}</span>
                    )}
                  </div>

                  {stream.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {stream.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {onViewMore && (
            <div className="flex justify-center pt-4 border-t">
              <Button
                variant="outline"
                onClick={onViewMore}
                className="w-full sm:w-auto"
              >
                <ArrowRight className="h-4 w-4 mr-2" />
                Browse All Streams
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}
